import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Upload, Users, Handshake, FileCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAbout() {
  const { t } = useTranslation();
  const [data, setData] = useState({
    description: '',
    team: [],
    partners: [],
    certifications: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/about-page`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/about-page`, data);
      toast.success('Запазено успешно');
    } catch (e) { toast.error('Грешка при запазване'); } finally { setSaving(false); }
  };

  const handleFileUpload = async (e, type, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${API}/upload?category=${type}&entity_id=about`, formData);
      const url = r.data.url?.startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      if (type === 'team') {
        const newTeam = [...data.team];
        newTeam[index].image = url;
        setData({ ...data, team: newTeam });
      } else if (type === 'partner') {
        const newPartners = [...data.partners];
        newPartners[index].logo = url;
        setData({ ...data, partners: newPartners });
      } else if (type === 'certification') {
        const newCerts = [...data.certifications];
        newCerts[index].url = url;
        newCerts[index].filename = file.name;
        setData({ ...data, certifications: newCerts });
      }
      toast.success(type === 'certification' ? 'Документът е качен' : 'Изображението е качено');
    } catch (e) { toast.error('Грешка при качване'); }
  };

  // Team handlers
  const addTeamMember = () => {
    setData({ ...data, team: [...data.team, { id: Date.now().toString(), name: '', role: '', image: '' }] });
  };
  const updateTeamMember = (index, field, value) => {
    const newTeam = [...data.team];
    newTeam[index][field] = value;
    setData({ ...data, team: newTeam });
  };
  const removeTeamMember = (index) => {
    setData({ ...data, team: data.team.filter((_, i) => i !== index) });
  };

  // Partner handlers
  const addPartner = () => {
    setData({ ...data, partners: [...data.partners, { id: Date.now().toString(), name: '', logo: '', url: '' }] });
  };
  const updatePartner = (index, field, value) => {
    const newPartners = [...data.partners];
    newPartners[index][field] = value;
    setData({ ...data, partners: newPartners });
  };
  const removePartner = (index) => {
    setData({ ...data, partners: data.partners.filter((_, i) => i !== index) });
  };

  // Certification handlers
  const addCertification = () => {
    setData({ ...data, certifications: [...data.certifications, { id: Date.now().toString(), name: '', url: '' }] });
  };
  const updateCertification = (index, field, value) => {
    const newCerts = [...data.certifications];
    newCerts[index][field] = value;
    setData({ ...data, certifications: newCerts });
  };
  const removeCertification = (index) => {
    setData({ ...data, certifications: data.certifications.filter((_, i) => i !== index) });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-about">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">{t('about_admin.page_title')}</h1>
          <p className="text-muted-foreground">{t('about_admin.page_subtitle')}</p>
        </div>
        <Button onClick={handleSave} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? 'Запазване...' : 'Запази'}</Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general">Общи</TabsTrigger>
          <TabsTrigger value="team"><Users className="w-4 h-4 mr-2" />{t('about_admin.team')}</TabsTrigger>
          <TabsTrigger value="partners"><Handshake className="w-4 h-4 mr-2" />{t('about_admin.partners')}</TabsTrigger>
          <TabsTrigger value="certs"><FileCheck className="w-4 h-4 mr-2" />{t('about_admin.certificates')}</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="admin-card space-y-4">
            <h3 className="font-heading font-semibold text-lg">{t('about_admin.description')}</h3>
            <div>
              <Label>{t('about_admin.company_text')}</Label>
              <Textarea value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} rows={5} className="mt-2" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg">{t('about_admin.our_team')}</h3>
              <Button variant="outline" size="sm" onClick={addTeamMember}><Plus className="w-4 h-4 mr-1" />Добави</Button>
            </div>
            <div className="space-y-4">
              {data.team?.map((member, index) => (
                <div key={member.id} className="flex items-start gap-4 p-4 bg-secondary rounded-lg">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                    {member.image ? <img src={member.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Users className="w-8 h-8" /></div>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input placeholder="Име" value={member.name} onChange={(e) => updateTeamMember(index, 'name', e.target.value)} />
                    <Input placeholder="Роля/Позиция" value={member.role} onChange={(e) => updateTeamMember(index, 'role', e.target.value)} />
                    <div className="flex gap-2">
                      <Input placeholder="URL на снимка" value={member.image} onChange={(e) => updateTeamMember(index, 'image', e.target.value)} className="flex-1" />
                      <label className="cursor-pointer"><input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'team', index)} />
                        <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-4 h-4" /></span></Button>
                      </label>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeTeamMember(index)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              {(!data.team || data.team.length === 0) && <p className="text-muted-foreground text-center py-8">Няма добавени членове на екипа</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="partners">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg">Партньори</h3>
              <Button variant="outline" size="sm" onClick={addPartner}><Plus className="w-4 h-4 mr-1" />Добави</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.partners?.map((partner, index) => (
                <div key={partner.id} className="flex items-start gap-3 p-4 bg-secondary rounded-lg">
                  <div className="w-16 h-12 rounded bg-white flex items-center justify-center flex-shrink-0 border">
                    {partner.logo ? <img src={partner.logo} alt="" className="max-w-full max-h-full object-contain" /> : <Handshake className="w-6 h-6 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input placeholder="Име на партньора" value={partner.name} onChange={(e) => updatePartner(index, 'name', e.target.value)} />
                    <Input placeholder="Линк към сайта" value={partner.url} onChange={(e) => updatePartner(index, 'url', e.target.value)} />
                    <div className="flex gap-2">
                      <Input placeholder="URL на лого" value={partner.logo} onChange={(e) => updatePartner(index, 'logo', e.target.value)} className="flex-1" />
                      <label className="cursor-pointer"><input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'partner', index)} />
                        <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-4 h-4" /></span></Button>
                      </label>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removePartner(index)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              {(!data.partners || data.partners.length === 0) && <p className="text-muted-foreground text-center py-8 col-span-2">Няма добавени партньори</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="certs">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading font-semibold text-lg">{t('about_admin.certifications')}</h3>
                <p className="text-sm text-muted-foreground mt-1">Качете PDF/изображение или добавете линк към документа</p>
              </div>
              <Button variant="outline" size="sm" onClick={addCertification}><Plus className="w-4 h-4 mr-1" />Добави</Button>
            </div>
            <div className="space-y-4">
              {data.certifications?.map((cert, index) => (
                <div key={cert.id} className="p-4 bg-secondary rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <FileCheck className="w-5 h-5 text-[#1B5E37] flex-shrink-0" />
                    <Input placeholder="Име на сертификата" value={cert.name} onChange={(e) => updateCertification(index, 'name', e.target.value)} className="flex-1" data-testid={`cert-name-${index}`} />
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeCertification(index)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex items-center gap-2 ml-8">
                    <Input placeholder="Линк към документа (URL)" value={cert.url} onChange={(e) => updateCertification(index, 'url', e.target.value)} className="flex-1" data-testid={`cert-url-${index}`} />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">или</span>
                    <label className="cursor-pointer flex-shrink-0">
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={(e) => handleFileUpload(e, 'certification', index)} />
                      <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-4 h-4 mr-1" />Качи файл</span></Button>
                    </label>
                  </div>
                  {cert.url && (
                    <div className="ml-8 flex items-center gap-2 text-sm">
                      <a href={cert.url} target="_blank" rel="noopener noreferrer" className="text-[#1B5E37] hover:underline flex items-center gap-1">
                        <FileCheck className="w-3.5 h-3.5" />
                        {cert.filename || 'Преглед на документа'}
                      </a>
                    </div>
                  )}
                </div>
              ))}
              {(!data.certifications || data.certifications.length === 0) && <p className="text-muted-foreground text-center py-8">Няма добавени сертификати</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
